import { Formik, Form, FormikProps } from 'formik';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import Bugsnag from '@bugsnag/js';

import { ADDRESS_DEFAULT_INITIAL_VALUES, CHINA_COUNTRY_CODE } from 'checkout/enums/address';
import { AddressFormBySchema } from 'checkout/components/AddressForm/AddressFormBySchema';
import { AddressFormChina } from 'checkout/components/AddressForm/AddressFormChina';
import { AddressTextField } from 'checkout/components/AddressForm/AddressTextField';
import { Button } from 'shared/components/Button';
import { Checkbox } from 'shared/components/Checkbox';
import CountryDropdownField from 'checkout/components/AddressForm/CountryDropdownField';
import { CreditCardInput } from 'checkout/components/Stripe/CreditCardInput';
import { FeatureFlags } from 'featureFlags/constants';
import { FieldType } from 'checkout/enums/FieldType';
import { getBillingAddressValidationSchema } from 'shared/utils/validations';
import { GiftCard } from 'checkout/components/Payment/GiftCard';
import { GuestEmailForm } from 'checkout/components/Account/GuestEmailForm';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { IAddress } from 'checkout/types/Address';
import { login } from 'store/slice/authSlice';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useCountriesQuery } from 'store/api/countries';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useLocation } from 'shared/hooks/useLocation';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.Billing.BillingFormCreditCard';

export const BillingFormCreditCard = ({
  CCbillingInfos,
  consentModal,
  formRef,
  setConsentModal,
  setIsAddingCreditCard,
}) => {
  const router = useRouter();

  const { data } = useCountriesQuery();
  const countriesData = data?.countries;
  const { isAuthenticated } = useStoreSelector((state) => state.auth);
  const storeDispatch = useStoreDispatch();

  const {
    addNewCreditCard,
    addresses,
    createGuestOrderFailureDispatcher,
    createGuestOrderRequestDispatcher,
    createGuestOrderSuccessDispatcher,
    isGuestCheckout,
    order,
  } = useCheckoutContext();
  const { selectedCountryCode } = useLocation();

  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isCheckoutOffersEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_CHECKOUT_OFFERS);
  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );

  const isOffer = !!order?.wantId && isCheckoutOffersEnabled;

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId: order?.product?.giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });

  const [chinaDistrict, setChinaDistrict] = useState({ display: '', value: '', zipcode: '' });
  const [countriesList, setCountriesList] = useState([]);
  const [country, setCountry] = useState({ display: '', value: '' });
  const [isAddressSameAsShipping, setIsAddressSameAsShipping] = useState(!isPurchasingGiftCard);
  const [isCnidCardChecked, setIsCnidCardChecked] = useState(true);
  const [isChinaSelected, setIsChinaSelected] = useState(false);
  const [isNewsletterSignUpChecked, setIsNewsletterSignUpChecked] = useState(false);
  const [isPostalCodeRequired, setIsPostalCodeRequired] = useState(true);
  const [isStateRequired, setIsStateRequired] = useState(true);
  const [state, setState] = useState({ display: '', value: '' });
  const [stripeComplete, setStripeComplete] = useState(false);

  const handleBillingInfoBackClick = (event) => {
    event.preventDefault();
    setIsAddingCreditCard(false);
  };

  const handleChinaCitizenIdCardChange = () => {
    setIsCnidCardChecked(!isCnidCardChecked);
  };

  const handleCountryMenuItemClick = (menuItem) => {
    const selectedCountry = countriesList.find((_country) => _country.value === menuItem?.value);

    setCountry(menuItem);
    setState({ display: '', value: '' });
    /**
     * NOTE: currently because our cc input is generated by stripe and our
     * billing address form is separate we have an issue when billing zip
     * is optional and cc zip is not. Therefore until a better solution is
     * made will continue to keep zip required
     */
    setIsPostalCodeRequired(true);

    setIsStateRequired(selectedCountry?.stateRequired ?? true);
  };

  const handleNewsletterSignUpChange = () => {
    setIsNewsletterSignUpChecked(!isNewsletterSignUpChecked);
  };

  const handleSameShippingChange =
    (currentFormValues: IAddress, setFormValues: (values: IAddress) => void) => () => {
      const name = currentFormValues.name || '';

      const shippingAddress = addresses[order?.addressId];

      if (isAddressSameAsShipping) {
        setIsAddressSameAsShipping(false);

        setFormValues({
          ...ADDRESS_DEFAULT_INITIAL_VALUES,
          countryCode: selectedCountryCode,
          name,
        });

        sendTrackingEvent('BILLING_SHIPPING_TOGGLE', { toggle_value: 'no' });

        return;
      }

      setIsAddressSameAsShipping(true);

      setFormValues({
        ...ADDRESS_DEFAULT_INITIAL_VALUES,
        ...shippingAddress,
        name,
      });

      sendTrackingEvent('BILLING_SHIPPING_TOGGLE', { toggle_value: 'yes' });
    };

  const handleSubmit = async (address: IAddress) => {
    if (isChinaSelected && isCnidCardChecked && !consentModal.consent) {
      setConsentModal({ display: true, consent: false });
      return;
    }

    if (isAddressSameAsShipping) {
      const shippingAddress = addresses[order?.addressId];

      await addNewCreditCard({
        name: address.name,
        address1: shippingAddress.address1,
        address2: shippingAddress.address2,
        city: shippingAddress.city,
        country: shippingAddress.country,
        countryCode: shippingAddress.countryCode,
        phone: shippingAddress.phone,
        postalCode: shippingAddress.postalCode,
        state: shippingAddress.state,
        stateCode: shippingAddress.stateCode,
      });

      return;
    }

    await addNewCreditCard({
      ...address,
      ...(isChinaSelected && { postalCode: chinaDistrict?.zipcode }),
    });
  };

  const handleGuestGiftCardPurchaseSubmit = async (input) => {
    if (isChinaSelected && isCnidCardChecked && !consentModal.consent) {
      setConsentModal({ display: true, consent: false });
      return;
    }

    createGuestOrderRequestDispatcher();

    let orderNumber = null;
    let guestUser;

    try {
      guestUser = await requestService({
        url: '/web-api/v1/create_guest',
        method: 'post',
        data: { guestEmail: input?.email, emailRegistration: isNewsletterSignUpChecked },
        withCredentials: true,
      });
      storeDispatch(login({ user: guestUser }));
    } catch (error) {
      createGuestOrderFailureDispatcher(error?.message);

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', {
          orderNumber,
          guestUser,
          giftCardId: router?.query?.giftCardId,
        });
      });
    }

    try {
      const guestOrder = await requestService({
        url: '/web-api/v1/orders/create_for_gift_card',
        method: 'post',
        data: {
          giftCardId: router?.query?.giftCardId,
          priceCents: router?.query?.priceCents,
        },
        withCredentials: true,
      });

      createGuestOrderSuccessDispatcher(guestOrder);

      orderNumber = guestOrder.number;

      router.query.orderNumber = orderNumber;

      await router.push(router, null, { shallow: true });

      await addNewCreditCard(
        {
          ...input,
          ...(isChinaSelected && { postalCode: chinaDistrict?.zipcode }),
        },
        orderNumber,
      );
    } catch (error) {
      createGuestOrderFailureDispatcher(error?.message);

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', {
          orderNumber,
          guestUser,
          giftCardId: router?.query?.giftCardId,
        });
      });
    }
  };

  useEffect(() => {
    if (!selectedCountryCode) return;

    ADDRESS_DEFAULT_INITIAL_VALUES.countryCode = selectedCountryCode;

    if (countriesData?.length > 0) {
      const shipsToCountry = countriesData.filter((_country) => _country.shipsTo);

      const transformedCountries = shipsToCountry.map((_country) => ({
        display: _country.name,
        value: _country.isoCode,
        zipRequired: _country.zipRequired,
        stateRequired: _country.stateRequired,
      }));

      setCountriesList(transformedCountries);

      const selectedCountry = transformedCountries.find(
        (_country) => _country.value === selectedCountryCode,
      );

      if (selectedCountry) {
        setCountry(selectedCountry);
      }
    }
  }, [countriesData?.length]);

  useEffect(() => {
    setIsChinaSelected(country?.value === CHINA_COUNTRY_CODE);
  }, [country?.value]);

  return (
    <Formik
      enableReinitialize
      initialValues={ADDRESS_DEFAULT_INITIAL_VALUES}
      innerRef={formRef}
      onSubmit={isGuestPurchasingGiftCard ? handleGuestGiftCardPurchaseSubmit : handleSubmit}
      validationSchema={getBillingAddressValidationSchema({
        isAddressRequired: !isAddressSameAsShipping,
        countryIsoCode: country?.value,
        isChinaCitizenIdCard: isCnidCardChecked,
        isPostalCodeRequired,
        isStateRequired,
      })}
    >
      {({ isSubmitting, values, setValues, isValid }: FormikProps<IAddress>) => (
        <BillingFormCreditCard.Form>
          {isGuestPurchasingGiftCard && (
            <GuestEmailForm
              isNewsletterSignUpChecked={isNewsletterSignUpChecked}
              handleNewsletterSignUpChange={handleNewsletterSignUpChange}
            />
          )}

          <AddressTextField
            type={FieldType.FullName}
            label={t(`${TP}.creditCardNameLabel`, 'Name on Card')}
            name="name"
            data-qa="checkout_payments_name_on_card"
          />

          <CreditCardInput onComplete={setStripeComplete} />

          {!isPurchasingGiftCard && (
            <BillingFormCreditCard.CheckboxWrapper
              htmlFor="same-shipping"
              aria-label={t(`${TP}.billingSameAsShippingLabel`, 'Same as Shipping')}
            >
              <Checkbox
                name="sameShipping"
                id="same-shipping"
                checked={isAddressSameAsShipping}
                label={t(`${TP}.paymentSame`, 'Billing address same as shipping')}
                onChange={handleSameShippingChange(values, setValues)}
              />
            </BillingFormCreditCard.CheckboxWrapper>
          )}

          {isPurchasingGiftCard && (
            <BillingFormCreditCard.Title>
              {t(`${TP}.billingAddress`, 'Billing Address')}
            </BillingFormCreditCard.Title>
          )}

          {!isAddressSameAsShipping && (
            <BillingFormCreditCard.AddressFormContainer $paddedTopAndBottom={isPurchasingGiftCard}>
              <BillingFormCreditCard.CountryDropdownField
                countriesList={countriesList}
                onMenuItemClick={handleCountryMenuItemClick}
                selectedCountry={country}
              />

              {!isChinaSelected && (
                <BillingFormCreditCard.PostalCode>
                  <AddressFormBySchema
                    country={country}
                    isPostalCodeRequired={isPostalCodeRequired}
                    onStateClick={(item) => setState(item)}
                    state={state}
                  />
                </BillingFormCreditCard.PostalCode>
              )}

              {isChinaSelected && (
                <AddressFormChina
                  chinaDistrict={chinaDistrict}
                  handleChinaCitizenIdCardChange={handleChinaCitizenIdCardChange}
                  isCnidCardChecked={isCnidCardChecked}
                  setChinaDistrict={setChinaDistrict}
                />
              )}
            </BillingFormCreditCard.AddressFormContainer>
          )}
          <BillingFormCreditCard.GiftCard />
          <BillingFormCreditCard.Button
            data-qa="checkout_payment_submit"
            title={
              isOffer
                ? t(`${TP}.continueToOfferReviewButton`, 'Continue to Offer Review')
                : t(`${TP}.continueToOrderReviewButton`, 'Continue to Order Review')
            }
            type="submit"
            disabled={
              !stripeComplete ||
              !values.name ||
              (!isAddressSameAsShipping && (!values.address1 || !isValid)) ||
              isSubmitting
            }
            loading={isSubmitting}
          />
          {!isEmpty(CCbillingInfos) && (
            <BillingFormCreditCard.Back
              href=""
              data-qa="checkout_payment_back"
              onClick={handleBillingInfoBackClick}
            >
              {t(`${TP}.backToPaymentsButton`, 'Back to Saved Payment Options')}
            </BillingFormCreditCard.Back>
          )}
        </BillingFormCreditCard.Form>
      )}
    </Formik>
  );
};

BillingFormCreditCard.Form = styled(Form)`
  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    padding-top: 24px;
  }
`;

BillingFormCreditCard.Button = styled(Button)`
  font-size: 11px;
  width: 100%;
  text-transform: uppercase;

  &:disabled {
    cursor: default;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    height: 42px;
    font-size: 11.5px;
  }
`;

BillingFormCreditCard.GiftCard = styled(GiftCard)`
  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin-top: 48px;
  }
`;

BillingFormCreditCard.Back = styled.a`
  display: block;
  text-align: center;
  margin-top: 24px;
  text-decoration: underline;
`;

BillingFormCreditCard.PostalCode = styled.div`
  padding-top: 20px;
`;

BillingFormCreditCard.CheckboxWrapper = styled.label`
  color: ${({ theme }) => theme.colors.black};
  display: flex;
  grid-template-columns: min-content auto;
  margin-block: 24px 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    font-size: 11.5px;
    margin-block: 40px 48px;
  }
`;

BillingFormCreditCard.Label = styled.p`
  margin: 2px 0 0 -8px;
`;

BillingFormCreditCard.CountryDropdownField = styled(CountryDropdownField)`
  padding-bottom: 4px;
`;

BillingFormCreditCard.AddressFormContainer = styled.div<{ $paddedTopAndBottom?: boolean }>`
  ${({ $paddedTopAndBottom }) =>
    $paddedTopAndBottom &&
    css`
      padding: 12px 0 48px;
    `};
`;

BillingFormCreditCard.Title = styled.p`
  margin-top: 48px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
`;
